import { type Product } from '../../hooks';

interface ProductCardProps {
  product: Product;
  categoryColor?: string;
  onClick?: () => void;
}

export function ProductCard({ product, categoryColor, onClick }: ProductCardProps) {
  const isOutOfStock = product.stock <= 0 && !product.variants?.length;
  const hasVariants = product.variants && product.variants.some((v) => v.isActive);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <button
      onClick={handleClick}
      disabled={isOutOfStock || !product.isActive}
      className={`
        relative bg-white rounded-lg border border-gray-200 
        overflow-hidden transition-all duration-200
        hover:border-primary-300 hover:shadow-md
        active:scale-[0.98]
        disabled:opacity-50 disabled:cursor-not-allowed
        disabled:hover:scale-100 disabled:hover:border-gray-200
        disabled:hover:shadow-none
        text-left w-full
      `}
    >
      <div 
        className="aspect-[4/3] relative"
        style={{ backgroundColor: categoryColor ? `${categoryColor}20` : '#f3f4f6' }}
      >
        {product.imageUrl ? (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-3xl">{product.imageUrl}</span>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span 
              className="text-3xl"
              style={{ color: categoryColor || '#9ca3af' }}
            >
              {product.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-medium text-xs">Habis</span>
          </div>
        )}
      </div>

      <div className="p-2">
        <h3 className="font-medium text-gray-900 truncate text-xs leading-tight">
          {product.name}
        </h3>
        <p className="text-primary-600 font-bold text-sm mt-0.5">
          {formatPrice(product.price)}
        </p>
        <p className="text-[10px] text-gray-400">
          {hasVariants ? `${product.variants?.filter(v => v.isActive).length || 0} varian` : `${product.stock} ${product.unit}`}
        </p>
      </div>
    </button>
  );
}
